import { useMemo } from 'react';

import { useApi as useCoreApi, type ApiFetchInit } from '../../../web/app-home/src/lib/api';
import { wrapThrowingApiFetch } from '../fetch-error';

export type { ApiFetchInit };

/**
 * Type-only re-export of the canonical `FetchError` shape (erased at compile
 * time). `FetchError` is declared in the dependency-free leaf
 * `mini-apps/_shared/fetch-error-types.ts`, which `mini-apps/_shared/fetch-error.ts`'s
 * `FetchErrorLike` also extends — see that file's top-of-file comment for
 * why the shape lives in a zero-import leaf instead of in
 * `web/app-home/src/lib/errors.ts` (a `tsc -p tsconfig.tests.json`
 * constraint). Precedented by `_shared/components/index.ts`'s re-exports.
 */
export type { FetchError } from '../fetch-error-types';

/**
 * Mini-app HTTP boundary: `apiFetch` (throwing) and `apiFetchRaw`
 * (passthrough), both backed by the same underlying core Terra `apiFetch`
 * (`web/app-home/src/lib/api.ts`) — so its body-inspecting side effects
 * (409 `slack_user_not_found` → sign-in redirect) always run first,
 * unaffected by anything below. A policy refusal (403
 * `read_only_impersonation`, 423 external-write sandbox) is toasted once by
 * core, which then rejects with a `WriteRefusedError` before either function
 * sees a Response, so BOTH reject with it; callers must not toast it again.
 *
 * Deliberate asymmetry vs core `apiFetch`: core otherwise resolves with the
 * raw Response, by design — ~130 core files and 37 web tests depend on
 * that contract, and changing it is explicitly out of scope here (see
 * `mini-apps/CLAUDE.md`). Mini-apps get a stricter, throwing contract
 * instead, the same way `_shared`'s other capabilities narrow/opinionate
 * on the core primitive (`db` exposes read-only accessors):
 *
 * - `apiFetch` — on success returns the raw Response untouched (callers
 *   keep their own `.json()` / stream reads — e.g. `useAiSummary` reads
 *   `res.body` as a stream, which pre-parsing would break). On `!res.ok`,
 *   parses the JSON error body and throws a structured `FetchErrorLike`
 *   (`code`/`oauthUrl`/`userMessage`/`status`) instead of resolving — see
 *   `../fetch-error.ts`. This makes "forgot to translate the HTTP error
 *   into a toast" structurally impossible for mini-app call sites: every
 *   failure becomes a classifiable error by construction. Network-level
 *   rejections (the underlying `fetch()` call itself rejecting) propagate
 *   unchanged, never wrapped into a `FetchErrorLike`.
 * - `apiFetchRaw` — the old return-the-Response contract, unthrown, for
 *   the handful of call sites that legitimately need to inspect a failed
 *   Response (404-means-null, retry-on-404 loops, SSE streams, body-detail
 *   parsing).
 */
export function useApi() {
  const { apiFetch: apiFetchRaw } = useCoreApi();

  const apiFetch = useMemo(() => wrapThrowingApiFetch(apiFetchRaw), [apiFetchRaw]);

  return { apiFetch, apiFetchRaw };
}
